import { useLoaderData } from 'react-router';

import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { DetailScrollReveal } from '~/ui-rocco/pages/detail-shared';
import { EditorialClosing } from '~/ui-rocco/pages/detail-shared';
import { ProfessionalSummarySection } from './sections/ProfessionalSummarySection';
import { ProfessionalOverviewAccordionSection } from './sections/ProfessionalOverviewAccordionSection';
import { ProfessionalAboutSection } from './sections/ProfessionalAboutSection';
import { ProfessionalSelectedWorkSection } from './sections/ProfessionalSelectedWorkSection';
import { ProfessionalTeamSection } from './sections/ProfessionalTeamSection';
import { ProfessionalNetworkSection } from './sections/ProfessionalNetworkSection';
import { ProfessionalArticlesSection } from './sections/ProfessionalArticlesSection';
import { ProfessionalContactSection } from './sections/ProfessionalContactSection';
import { getSelectedShowcase } from './utils/featured-work';
import { getOverviewSectionNumbers } from './utils/section-numbering';
import { useCanBookDirectMeeting } from './utils/book-meeting';

/**
 * BC-4505 — revamped editorial professional details page.
 *
 * Built section-by-section per `plans/BC-4505-details-page-revamp.md`, composed
 * from the shared `detail-shared/` library + professional-specific pieces.
 * Rendered in place of the legacy overview page behind the
 * `details_revamp_2026` feature flag (or `?revamp=1` in local dev).
 *
 * Pending data wiring (loader aggregation / codegen): (02) Team, (04) Articles,
 * the project-value stat, and the (03) Who-we-work-with partner block.
 */
export const ProfessionalPageRevamp = () => {
    const {
        professionalOverview,
        featuredProjects,
        featuredProducts,
        teamMembers,
        networkProfessionals,
        articleTiles,
    } = useLoaderData<typeof loader>();

    // Section "(0N)" numbers run as one continuous, gap-free sequence down the
    // page; each `has*` here mirrors the matching section's own self-hide guard.
    const hasBookMeeting = useCanBookDirectMeeting(
        professionalOverview.ClientDirectorCalendarSession,
    );
    const hasTestimonials = (
        professionalOverview.professionalTestimonials ?? []
    ).some((testimonial) => testimonial.Content);
    // The Overview accordion self-hides when it has no rows; render its leading
    // spacer with it so we don't leave a double gap above About in that case.
    const showOverviewAccordion = hasBookMeeting || hasTestimonials;

    // Each optional section pairs its leading spacer with itself (below), so an
    // empty section drops its gap too — otherwise two spacers stack into a
    // double gap above the next section.
    const hasTeam = (teamMembers ?? []).length > 0;
    const hasNetwork = (networkProfessionals ?? []).length > 0;
    const hasArticles = (articleTiles ?? []).length > 0;

    const numbers = getOverviewSectionNumbers({
        hasBookMeeting,
        hasTestimonials,
        hasSelectedWork:
            getSelectedShowcase(
                featuredProjects.featuredProjectsList,
                featuredProducts.featuredProductsList,
                Boolean(professionalOverview.IsManufacturer),
            ).items.length > 0,
        hasTeam,
        hasNetwork,
        hasArticles,
    });

    return (
        <main
            id="main-content"
            data-testid="professional-page-revamp"
            className="bg-white"
        >
            {/* Sub-nav → first content = 48px on desktop, the same hero ↔
                content gap as the project and product detail pages. Every tab's
                first block starts at this offset so the heading + CTA cluster
                never jumps between tabs. */}
            <SectionSpacer
                height={48}
                mobileHeight={24}
                unit="rem"
                className="bg-white"
            />
            <ProfessionalSummarySection />
            {showOverviewAccordion && (
                <>
                    <SectionSpacer
                        height={160}
                        mobileHeight={36}
                        unit="rem"
                        className="bg-white"
                    />
                    <ProfessionalOverviewAccordionSection />
                </>
            )}
            {/* Featured project image sits ~170px (page spacer + 40px) below the
                accordion's last item. */}
            <SectionSpacer
                height={170}
                mobileHeight={36}
                className="bg-white"
                unit="rem"
            />
            {/* About + Selected work share one continuous dark background. */}
            <ProfessionalAboutSection />
            <ProfessionalSelectedWorkSection
                sectionNumber={numbers.selectedWork}
            />
            {hasTeam && (
                <>
                    <SectionSpacer
                        height={160}
                        mobileHeight={40}
                        className="bg-white"
                        unit="rem"
                    />
                    <DetailScrollReveal>
                        <ProfessionalTeamSection sectionNumber={numbers.team} />
                    </DetailScrollReveal>
                </>
            )}
            {hasNetwork && (
                <>
                    <SectionSpacer
                        height={160}
                        mobileHeight={40}
                        className="bg-white"
                        unit="rem"
                    />
                    <DetailScrollReveal>
                        <ProfessionalNetworkSection
                            sectionNumber={numbers.network}
                        />
                    </DetailScrollReveal>
                </>
            )}
            {hasArticles && (
                <>
                    <SectionSpacer
                        height={160}
                        mobileHeight={40}
                        className="bg-white"
                        unit="rem"
                    />
                    <DetailScrollReveal>
                        <ProfessionalArticlesSection
                            sectionNumber={numbers.articles}
                        />
                    </DetailScrollReveal>
                </>
            )}
            <ProfessionalContactSection />
            <EditorialClosing hideHero />
        </main>
    );
};
